import React from "react";
import { styled } from "@mui/material/styles";
import { graphql } from "react-relay";
import { useNavigate, useParams } from "react-router-dom";
import { useLazyLoadQuery } from "react-relay/hooks";
import { Typography, Tabs, Tab } from "@mui/material";

import { GeneralTaskPageQuery } from "./__generated__/GeneralTaskPageQuery.graphql";
import GeneralTaskChildrenTab from "./tabs/GeneralTaskChildrenTab";
import GeneralTaskDetailTab from "./tabs/GeneralTaskDetailTab";
import TabPanel from "../../components/common/TabPanel";
import { GeneralTaskParams, GeneralTaskKeyValueListPairs } from "./generalTask.types";
import SimpleBackdrop from "../../components/common/SimpleBackdrop";
import CentredCircularProgress from "../../components/common/CentredCircularProgress";
import PageContainer from "../../components/common/PageContainer";
import TabContainer from "../../components/common/TabContainer";

const StyledTab = styled(Tab)(({ theme }) => ({
  maxWidth: "5vw",
  borderRight: `2px solid ${theme.palette.divider}`,
}));

const Title = styled(Typography)(() => ({
  paddingBottom: "20px",
}));

export const sweepsList = (
  arg_lists: GeneralTaskKeyValueListPairs,
  sweeps: readonly (string | null | undefined)[],
): GeneralTaskKeyValueListPairs => {
  if (arg_lists) return arg_lists.filter((el) => sweeps.includes(el ? el.k : ""));
  return [];
};

const GeneralTask: React.FC = () => {
  const { id, tab } = useParams<GeneralTaskParams>();
  // Trimmed because a URL carrying stray whitespace around the id (e.g. a pasted
  // link with a leading %20) still resolves server-side, but gives this query and
  // GeneralTaskChildrenTab's query different `node(id:)` root keys in the Relay
  // store, so the general task's fields go missing from whichever ran first.
  const validatedId: string = (id ?? "").trim();
  const data = useLazyLoadQuery<GeneralTaskPageQuery>(generalTaskPageQuery, {
    id: validatedId,
  });

  const navigate = useNavigate();

  // Memoised so consumers get a stable reference; recomputed only when the underlying
  // task arguments change, not on every render of this page.
  const sweepArgs = React.useMemo(
    () => sweepsList(data?.node?.argument_lists ?? [], data?.node?.swept_arguments ?? [""]),
    [data?.node?.argument_lists, data?.node?.swept_arguments],
  );

  if (!data?.node) {
    return (
      <Typography variant="h5" gutterBottom>
        General Task: Id Not Found
      </Typography>
    );
  }

  const renderTab = () => {
    switch (tab) {
      default:
        return (
          <TabPanel>
            <GeneralTaskDetailTab sweepArgs={sweepArgs} data={data} />
          </TabPanel>
        );
      case "ChildTasks":
        return (
          <TabPanel>
            <React.Suspense fallback={<CentredCircularProgress />}>
              <GeneralTaskChildrenTab generalTaskData={data} sweepArgs={sweepArgs} />
            </React.Suspense>
          </TabPanel>
        );
    }
  };

  return (
    <PageContainer maxWidth="xl">
      <Typography variant="h5" gutterBottom>
        General Task: {data?.node?.id}
      </Typography>
      {data?.node?.title && <Title variant="h4">{data?.node?.title}</Title>}
      <TabContainer maxWidth="xl">
        <Tabs
          orientation="vertical"
          value={tab ?? "DetailsTab"}
          onChange={(_e, val) => navigate(`/GeneralTask/${validatedId}/${val}`)}
        >
          <StyledTab label="Details" id="vertical-tab-0" aria-controls="vertical-tabpannel-0" value={"DetailsTab"} />
          <StyledTab
            label="Child Tasks"
            id="vertical-tab-1"
            aria-controls="vertical-tabpannel-1"
            value={"ChildTasks"}
          />
        </Tabs>
        {renderTab()}
      </TabContainer>
    </PageContainer>
  );
};

export const GeneralTaskPage: React.FC = () => {
  return <React.Suspense fallback={<SimpleBackdrop />}>{<GeneralTask />}</React.Suspense>;
};

export default GeneralTaskPage;

const generalTaskPageQuery = graphql`
  query GeneralTaskPageQuery($id: ID!) {
    node(id: $id) {
      ... on GeneralTask {
        id
        title
        description
        notes
        created
        updated
        agent_name
        model_type
        subtask_type
        subtask_count
        subtask_result
        argument_lists {
          k
          v
        }
        swept_arguments
        children {
          total_count
        }
      }
    }
  }
`;
